import { type QConfigTableData } from '@exam-management/common/types';
import { QuestionTypeChip } from '@exam-management/components';
import { QConfigStatus } from '@exam-management/graphql';
import { styled, TableCell, TableRow, type TableRowProps } from '@shared/components';
import { useHasPermission } from '@shared/permissions';

import { EDIT_QCONFIG_REQUIRED_PERMISSIONS } from '../../constants';
import { getQuestionChipType } from '../utils';

import { ChapterTableRowMenu } from './chapter-table-row-menu';
import { renderChapterNameContent } from './generate';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderRight: '1px solid',
  borderColor: theme.palette.grey[300],
}));

interface ChapterTableRowProps extends TableRowProps {
  data: QConfigTableData;
  expanded?: boolean;
  onExpand?: (id: string) => void;
  onToggle?: (id: string) => void;
  status: QConfigStatus;
}

export default function ChapterTableRow(props: Readonly<ChapterTableRowProps>) {
  const { data, expanded: isExpanded = false, onExpand, onToggle, status, ...restProps } = props;
  const { refCode, totalQuestions, scorePerQuestion, questionType, calculated, notCalculated } =
    data;
  const questionChipType = getQuestionChipType(questionType);
  const isDraftQConfig = status === QConfigStatus.Draft;
  const hasPermissionForEditingQConfig = useHasPermission(EDIT_QCONFIG_REQUIRED_PERMISSIONS);

  return (
    <TableRow
      {...restProps}
      sx={(theme) => ({
        backgroundColor: isExpanded
          ? theme.palette.primary.lighter
          : theme.palette.background.paper,
        ':hover': { backgroundColor: theme.palette.grey[50] },
        ...restProps.sx,
      })}
    >
      <StyledTableCell
        sx={{
          p: (theme) => theme.spacing(0),
        }}
      >
        {renderChapterNameContent(
          data,
          isExpanded,
          onToggle,
          hasPermissionForEditingQConfig && isDraftQConfig,
        )}
      </StyledTableCell>
      <StyledTableCell>{refCode ?? '-'}</StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          fontWeight: 600,
        }}
      >
        {totalQuestions ?? '-'}
      </StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          fontWeight: 600,
        }}
      >
        {scorePerQuestion}
      </StyledTableCell>
      <StyledTableCell align='center'>
        {questionChipType ? <QuestionTypeChip type={questionChipType} /> : '-'}
      </StyledTableCell>
      <StyledTableCell align='center'>{calculated.easy ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{calculated.medium ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{calculated.hard ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{notCalculated.easy ?? '-'}</StyledTableCell>
      <StyledTableCell align='center'>{notCalculated.medium ?? '-'}</StyledTableCell>
      <StyledTableCell
        align='center'
        sx={{
          borderRight: !(isDraftQConfig && hasPermissionForEditingQConfig) ? 'none' : undefined,
        }}
      >
        {notCalculated.hard ?? '-'}
      </StyledTableCell>
      {isDraftQConfig && hasPermissionForEditingQConfig ? (
        <StyledTableCell
          align='center'
          sx={{
            borderRight: 'none',
          }}
        >
          <ChapterTableRowMenu chapterId={data.id} setExpandedRow={onExpand} />
        </StyledTableCell>
      ) : null}
    </TableRow>
  );
}
